import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createOrder, getShop } from '../utils/api';

const SERVICES = [
  { id: 'washing', label: '🫧 Washing' },
  { id: 'dry_cleaning', label: '👔 Dry Cleaning' },
  { id: 'laundry', label: '🧺 Full Laundry' },
  { id: 'ironing', label: '♨️ Ironing' },
];

const STATUS_STEPS = ['pending', 'accepted', 'picked_up', 'in_progress', 'delivered'];

export default function NewOrderPage() {
  const [params] = useSearchParams();
  const targetShopId = params.get('shop');
  const [shopInfo, setShopInfo] = useState(null);
  const [step, setStep] = useState(1);
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [clothesCount, setClothesCount] = useState(1);
  const [services, setServices] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (targetShopId) {
      getShop(targetShopId).then(r => setShopInfo(r.data)).catch(() => {});
    }
  }, [targetShopId]);

  const getLocation = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLoading(false);
        setStep(2);
      },
      () => { setError('Location access denied. Please enter address manually.'); setLoading(false); }
    );
  };

  const toggleService = (id) => {
    setServices(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!services.length) return setError('Select at least one service');
    if (!address.trim()) return setError('Enter your pickup address');
    setLoading(true); setError('');
    try {
      const res = await createOrder({
        clothes_count: clothesCount,
        services,
        notes,
        customer_location: {
          type: 'Point',
          coordinates: [location?.lng || 77.5946, location?.lat || 12.9716]
        },
        customer_address: address,
        target_shop_id: targetShopId || null
      });
      setSubmitted(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to place order');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="page" style={{ maxWidth: 560, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>🎉</div>
        <h1 style={{ marginBottom: 8 }}>Order Placed!</h1>
        <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
          {targetShopId
            ? 'Your order has been sent to the selected shop.'
            : `Your request was sent to ${submitted.notified_shops?.length || 'nearby'} shops. A shop will accept shortly.`}
        </p>
        <div className="card" style={{ textAlign: 'left', marginBottom: 24 }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Order ID</span>
              <code style={{ fontSize: '0.82rem' }}>{submitted.id?.slice(-8)}</code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Clothes</span>
              <strong>{submitted.clothes_count} items</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Services</span>
              <span>{submitted.services?.join(', ')}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Status</span>
              <span className="badge badge-pending">Pending</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => navigate('/customer/dashboard')}>View My Orders</button>
          <button className="btn btn-outline" onClick={() => { setSubmitted(null); setStep(1); setServices([]); setNotes(''); setClothesCount(1); }}>Place Another</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ maxWidth: 600 }}>
      <div className="page-header">
        <h1>New Laundry Order</h1>
        {shopInfo && <p style={{ color: 'var(--primary)', marginTop: 4 }}>📍 Ordering from: <strong>{shopInfo.name}</strong></p>}
        {!shopInfo && <p style={{ color: 'var(--text-muted)' }}>We'll broadcast your order to nearby shops</p>}
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center' }}>
        {['Location', 'Details', 'Review'].map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: step > i + 1 ? 'var(--success)' : step === i + 1 ? 'var(--primary)' : 'var(--border)',
              color: step >= i + 1 ? 'white' : 'var(--text-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem', fontWeight: 700
            }}>{step > i + 1 ? '✓' : i + 1}</div>
            <span style={{ fontSize: '0.85rem', color: step === i + 1 ? 'var(--text)' : 'var(--text-muted)', fontWeight: step === i + 1 ? 600 : 400 }}>{s}</span>
            {i < 2 && <div style={{ width: 24, height: 2, background: 'var(--border)' }} />}
          </div>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Step 1: Location */}
      {step === 1 && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: 16 }}>📍 Your Pickup Location</h3>
            {!location ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: 12 }}>📱</div>
                <p style={{ marginBottom: 20, color: 'var(--text-muted)' }}>
                  Allow location access so we can find shops near you
                </p>
                <button className="btn btn-primary" onClick={getLocation} disabled={loading}>
                  {loading ? 'Getting location...' : '📍 Use My Location'}
                </button>
              </div>
            ) : (
              <div className="alert alert-success">✅ Location detected! Lat: {location.lat.toFixed(4)}, Lng: {location.lng.toFixed(4)}</div>
            )}
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">Pickup Address</label>
              <textarea className="form-control" placeholder="Enter your full address for pickup..."
                value={address} onChange={e => setAddress(e.target.value)} rows={3} />
            </div>
            <button className="btn btn-primary btn-full" onClick={() => { if (!address.trim()) { setError('Enter your address'); return; } setError(''); setStep(2); }}>
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Details */}
      {step === 2 && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: 20 }}>🧺 Order Details</h3>
            <div className="form-group">
              <label className="form-label">Number of Clothes</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setClothesCount(Math.max(1, clothesCount - 1))}>−</button>
                <span style={{ fontSize: '1.4rem', fontWeight: 700, minWidth: 40, textAlign: 'center' }}>{clothesCount}</span>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setClothesCount(clothesCount + 1)}>+</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Services Needed</label>
              <div className="service-options">
                {SERVICES.map(s => (
                  <div key={s.id}
                    className={`service-chip ${services.includes(s.id) ? 'selected' : ''}`}
                    onClick={() => toggleService(s.id)}>
                    {s.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Special Instructions (optional)</label>
              <textarea className="form-control" placeholder="e.g. Handle with care, allergic to certain detergents..."
                value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { if (!services.length) { setError('Select at least one service'); return; } setError(''); setStep(3); }}>
                Review Order →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-header"><h3>📋 Review Your Order</h3></div>
            <div className="card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Pickup Address</span>
                  <strong style={{ maxWidth: '55%', textAlign: 'right', fontSize: '0.9rem' }}>{address}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Number of clothes</span>
                  <strong>{clothesCount} items</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Services</span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {services.map(s => <span key={s} className="badge badge-primary">{s.replace('_', ' ')}</span>)}
                  </div>
                </div>
                {notes && (
                  <div style={{ padding: '8px 0' }}>
                    <span style={{ color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notes</span>
                    <span style={{ fontSize: '0.9rem' }}>{notes}</span>
                  </div>
                )}
              </div>
              <div className="alert alert-info" style={{ marginTop: 16 }}>
                {targetShopId ? '🏪 This order will be sent directly to the selected shop.' : '📡 Your order will be broadcast to up to 5 nearby shops.'}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setStep(2)}>← Back</button>
                <button type="submit" className="btn btn-accent" style={{ flex: 1 }} disabled={loading}>
                  {loading ? 'Placing Order...' : '🚀 Place Order'}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
